/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import Swiper from 'swiper';
import BaseControl from 'scripts/base-control';
import DomEventsManager from 'scripts/dom-events-manager';
import { Slides } from './hero.models';
import paginationBullet from './hero-pagination-bullet.stache';
import heroSlide from './hero-slide.stache';
import deviceUtil from 'scripts/utils/device';
import cloudinaryUtil from 'scripts/utils/cloudinary';
import domUtil from 'scripts/utils/dom';
import dtm from 'scripts/services/dtm';

export const HeroModule = BaseControl.extend({
    init() {
        this._super();

        this.$swiperContainer = this.$element.find('.swiper-container');
        this.$swiperWrapper = this.$swiperContainer.find('.swiper-wrapper');

        this.heroSlides = new Slides(this.options.slides);
        const isVictoryClubPage = this.options.isVictoryClubPage;
        this.heroSlides.each(
            function(slide, index) {
                const imageType = _.get(slide, 'image.type');
                let imageSrc;

                if (imageType === 'imageGeneric') {
                    imageSrc = _.get(slide, 'image.url');
                } else if (imageType === 'imageCloudinary') {
                    imageSrc = cloudinaryUtil.getCloudinaryPath(
                        _.get(slide, 'image.identifier'),
                    );
                } else {
                    console.error(`Wrong image type ${imageType}`);
                    return true;
                }

                this.$swiperWrapper.append(
                    heroSlide({
                        imageSrc,
                        action: slide.action,
                        imageType: imageType,
                        imageCrop: _.get(slide, 'image.crop'),
                        imageId: _.get(slide, 'image.identifier'),
                        imageAlt: _.get(slide, 'image.alt') || '',
                        title: slide.title,
                        slideNum: index + 1,
                        live: slide.live,
                        isVictoryClubPage,
                    }),
                );
            }.bind(this),
        );

        if (this.options.slides.length >= 1) {
            setTimeout(() => {
                this.initSwiper();
                DomEventsManager.subscribe(this);
            }, 0);
        }
    },

    onScreenOrientation() {
        // set 300 ms delay to update iPhone
        // after rotation animation is finished
        _.delay(this.initSwiper.bind(this), 300);
    },

    onLayout() {
        this.element
            .find('.swiper-wrapper .swiper-slide')
            .each((index, slide) => {
                let image = $(slide).find('.s-slide-media img');
                if (image.data('image-type') === 'imageCloudinary') {
                    let src = cloudinaryUtil.getCloudinaryPath(
                        image.data('image-id'),
                        domUtil.getElementCssProperty(image, 'max-width'),
                        domUtil.getElementCssProperty(image, 'max-height'),
                        image.data('image-crop'),
                    );
                    image.attr('src', src);
                }
            });

        const preloader = this.element.find('.js-preloader');
        this.element.find('img').each((index, image) => {
            const newImage = new Image();
            newImage.src = image.src;
            $(newImage).one('load', () => {
                if (preloader) {
                    preloader.delay(2000).fadeOut(500);
                    setTimeout(() => preloader.remove(), 2500);
                }
            });
        });
    },

    updateSlideClasses: function(isDisplay) {
        var listSlides = this.$swiperContainer.find('.swiper-slide');
        if (listSlides.length > 1) {
            listSlides.each(function() {
                $(this)
                    .find('.hero-swiper-pagination-data')
                    .toggleClass('hero-inactive-slide-hidden', isDisplay);
                $(this)
                    .find('.s-slide-btn')
                    .toggleClass('hero-inactive-slide-hidden', isDisplay);
            });
        }
    },

    getSwiperOptions() {
        let autoRotation = this.options.autoRotation,
            rotationInterval = parseInt(this.options.autoRotationInterval, 10),
            slides = this.heroSlides;

        if (!_.isInteger(rotationInterval)) {
            rotationInterval = 0;
        }

        return {
            effect: 'fade',
            loop: slides && slides.length > 1,
            autoplay: autoRotation && rotationInterval ? rotationInterval : 0,
            autoplayDisableOnInteraction: true,
            simulateTouch: true,
            paginationClickable: true,
            speed: 1000,
            pagination: {
                el: this.$swiperContainer.find('.swiper-pagination'),
                clickable: true,
                renderBullet: (index, className) => {
                    const isActive = !index;
                    return this.$('<div>')
                        .append(
                            paginationBullet({
                                index: index,
                                className: className,
                                slideOptions: slides[index],
                                isActive: isActive,
                            }),
                        )
                        .html();
                },
            },
            navigation: {
                prevEl: this.$swiperContainer.find('.swiper-button-prev'),
                nextEl: this.$swiperContainer.find('.swiper-button-next'),
            },
            on: {
                init: function() {
                    this.updateSlideClasses(true);
                }.bind(this),
            },
        };
    },

    initSwiper() {
        let options = this.getSwiperOptions();

        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        if (!deviceUtil.isMobile) {
            options.preventClicks = false;
            options.preventClicksPropagation = false;
        }

        this.swiper = new Swiper(this.$swiperContainer, options);

        this.swiper.on(
            'slideChangeTransitionStart',
            function() {
                this.updateSlideClasses(false);

                // add current-page attr
                var current = this.swiper.realIndex || 0;
                var listPaginationButtons = this.$swiperContainer.find(
                    '.hero-swiper-pagination-button',
                );
                if (listPaginationButtons && listPaginationButtons.length > 0) {
                    listPaginationButtons.removeAttr('aria-current');
                    if (listPaginationButtons.length > 1) {
                        listPaginationButtons.each(function() {
                            if ($(this).index() === current) {
                                $(this).attr('aria-current', 'page');
                            }
                        });
                    } else {
                        listPaginationButtons
                            .eq(current)
                            .attr('aria-current', 'page');
                    }
                }
            }.bind(this),
        );
        this.swiper.on(
            'slideChangeTransitionEnd',
            function() {
                this.updateSlideClasses(true);
            }.bind(this),
        );
    },

    '.js-vc-analytics-hero-img click'(el) {
        const title = el.attr('data-analytics-hero-img-name');
        const slideNumber = el.attr('data-analytics-hero-carousel-number');
        const btnTitle = el
            .parent()
            .find('.js-vc-analytics-hero-btn')
            .attr('card-vc-hero-img-cta');

        dtm.trackHeroSlideClick(title, btnTitle, slideNumber);
    },

    '.js-vc-analytics-hero-btn click'(el) {
        const btnTitle = el.attr('card-vc-hero-img-cta');
        const slideNumber = el.attr('card-vc-hero-carousel-number');
        const title = el
            .parent()
            .parent()
            .find('.js-vc-analytics-hero-img')
            .attr('data-analytics-hero-img-name');

        dtm.trackHeroSlideClick(title, btnTitle, slideNumber);
    },
});
